'use client'

import { useEffect, useRef, useState } from 'react'

export type ChatItemCard = { name: string; price: string; note?: string }

export type ChatLine = {
  from: 'user' | 'ai'
  text?: string
  time?: string
  kind?: 'text' | 'voice' | 'items' | 'track'
  items?: ChatItemCard[]
  track?: { title: string; detail: string }
}

type Props = {
  name: string
  initials: string
  status?: string
  lines: ChatLine[]
  loopPauseMs?: number
  className?: string
  /** farori = owner light product UI; wa = customer WhatsApp */
  variant?: 'farori' | 'wa'
  showLang?: boolean
}

/** Looping chat demo — Farori (owner) or WhatsApp (customer). */
export default function AnimatedChatDemo({
  name,
  initials,
  status = 'online',
  lines,
  loopPauseMs = 3200,
  className = '',
  variant = 'wa',
  showLang = false,
}: Props) {
  const [visible, setVisible] = useState(0)
  const [typing, setTyping] = useState(false)
  const bodyRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!lines.length) return
    let cancelled = false
    let timer: ReturnType<typeof setTimeout>

    const step = (i: number) => {
      if (cancelled) return
      if (i >= lines.length) {
        setTyping(false)
        timer = setTimeout(() => {
          if (cancelled) return
          setVisible(0)
          step(0)
        }, loopPauseMs)
        return
      }
      const next = lines[i]
      if (next.from === 'ai' && i > 0) {
        setTyping(true)
        timer = setTimeout(() => {
          if (cancelled) return
          setTyping(false)
          setVisible(i + 1)
          timer = setTimeout(() => step(i + 1), next.kind === 'items' ? 2200 : 1600)
        }, 850)
      } else {
        setTyping(false)
        setVisible(i + 1)
        timer = setTimeout(() => step(i + 1), next.kind === 'voice' ? 1800 : 1400)
      }
    }

    setVisible(0)
    timer = setTimeout(() => step(0), 400)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [lines, loopPauseMs])

  useEffect(() => {
    const el = bodyRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
  }, [visible, typing])

  const shown = lines.slice(0, visible)
  const isFarori = variant === 'farori'

  return (
    <div
      className={`ai-chat-demo ${isFarori ? 'ai-chat-demo--farori' : 'ai-chat-demo--wa'} ${className}`.trim()}
      aria-hidden="true"
    >
      <div className="ai-chat-demo__head">
        <div className="ai-chat-demo__head-left">
          {isFarori ? <span className="ai-chat-demo__back" aria-hidden>‹</span> : null}
          <span className="ai-chat-demo__avatar">{initials}</span>
          <div>
            <p className="ai-chat-demo__name">
              {name}
              <span className="ai-chat-demo__dot" />
            </p>
            {!isFarori ? (
              <p className="ai-chat-demo__status">{status}</p>
            ) : null}
          </div>
        </div>
        {isFarori || showLang ? (
          <div className="ai-chat-demo__head-right">
            <span className="ai-chat-demo__lang">English ▾</span>
            <span className="ai-chat-demo__head-icon" aria-hidden>
              {isFarori ? '○' : '☰'}
            </span>
          </div>
        ) : null}
      </div>

      <div className="ai-chat-demo__body" ref={bodyRef}>
        {shown.map((line, idx) => {
          const side =
            line.from === 'user'
              ? 'ai-chat-demo__bubble ai-chat-demo__bubble--user'
              : 'ai-chat-demo__bubble ai-chat-demo__bubble--ai'

          if (line.kind === 'voice') {
            return (
              <div key={idx} className={`${side} ai-chat-demo__bubble--voice`}>
                <span className="ai-chat-demo__mic" aria-hidden>
                  ▶
                </span>
                <span className="ai-chat-demo__wave" aria-hidden />
                <p>{line.text || 'Voice message'}</p>
                {line.time ? <time>{line.time}</time> : null}
              </div>
            )
          }

          if (line.kind === 'items' && line.items?.length) {
            return (
              <div key={idx} className={`${side} ai-chat-demo__bubble--items`}>
                {line.text ? <p className="ai-chat-demo__items-intro">{line.text}</p> : null}
                <ul className="ai-chat-demo__items">
                  {line.items.map((it) => (
                    <li key={it.name}>
                      <span className="ai-chat-demo__item-name">{it.name}</span>
                      <span className="ai-chat-demo__item-price">{it.price}</span>
                      {it.note ? <span className="ai-chat-demo__item-note">{it.note}</span> : null}
                    </li>
                  ))}
                </ul>
                {line.time ? <time>{line.time}</time> : null}
              </div>
            )
          }

          if (line.kind === 'track' && line.track) {
            return (
              <div key={idx} className={`${side} ai-chat-demo__bubble--track`}>
                <p className="ai-chat-demo__track-title">{line.track.title}</p>
                <p className="ai-chat-demo__track-detail">{line.track.detail}</p>
                {line.time ? <time>{line.time}</time> : null}
              </div>
            )
          }

          return (
            <div key={idx} className={side}>
              <p>{line.text}</p>
              {line.time ? <time>{line.time}</time> : null}
            </div>
          )
        })}
        {typing ? (
          <div className="ai-chat-demo__bubble ai-chat-demo__bubble--typing">
            <span />
            <span />
            <span />
          </div>
        ) : null}
      </div>

      {isFarori ? (
        <div className="ai-chat-demo__composer">
          <span className="ai-chat-demo__plus" aria-hidden>
            +
          </span>
          <span className="ai-chat-demo__input">Type</span>
          <span className="ai-chat-demo__mic-btn" aria-hidden>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
              <path d="M12 14a3 3 0 0 0 3-3V6a3 3 0 1 0-6 0v5a3 3 0 0 0 3 3zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.93V21h2v-3.07A7 7 0 0 0 19 11h-2z" />
            </svg>
          </span>
        </div>
      ) : (
        <div className="ai-chat-demo__composer ai-chat-demo__composer--wa">
          <span className="ai-chat-demo__plus" aria-hidden>
            +
          </span>
          <span className="ai-chat-demo__input">Message</span>
          <span className="ai-chat-demo__mic-btn" aria-hidden>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
              <path d="M12 14a3 3 0 0 0 3-3V6a3 3 0 1 0-6 0v5a3 3 0 0 0 3 3zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.93V21h2v-3.07A7 7 0 0 0 19 11h-2z" />
            </svg>
          </span>
        </div>
      )}
    </div>
  )
}
